import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Headers, Http, Request, RequestOptions, Response, RequestMethod } from '@angular/http';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/throw';

import { environment } from '../../environments/environment';
import { CfResponse } from '../../app/class/cf-response';

/***************
 * セッションIDを管理するための内部関数。
 * セッションIDの保管にはcookieを使用する。LocalStorageではプロトコル（http/https）間で同じ領域を共有できないため。
 ***************/
const SessionManager = {
  setSessionId: function (sessionId) {
    if (typeof sessionId !== 'string') {
      return;
    }
    if (!window.navigator.cookieEnabled) {
      return;
    }

    document.cookie = environment.webapi.sessionHeaderKey + '=' + sessionId + '; path=/oauth';
  },
  getSessionId: function () {
    if (!window.navigator.cookieEnabled) {
      return;
    }

    let sessionId = null;
    const cookies = document.cookie.split('; ');
    for (let i = 0; i < cookies.length; i++) {
      const separatorIdx = cookies[i].indexOf('=');
      const key = cookies[i].substr(0, separatorIdx);
      const value = cookies[i].substr(separatorIdx + 1);
      if (key === environment.webapi.sessionHeaderKey) {
        sessionId = value;
        break;
      }
    }
    return sessionId;
  }
};

/***************
* トークンを管理するための内部関数。
* トークンの保管にはメモリを使用する。
***************/
export const TokenManager = {
  token: '',
  setToken: function (token) {
    if (typeof token !== 'string') {
      return;
    }
    // 返却されたトークンの値でhiddenタグを更新
    const tokenElement = document.getElementById('#' + environment.webapi.tokenHeaderKey);
    if (tokenElement != null) {
      const separatorIdx = token.indexOf('=');
      tokenElement.setAttribute('name', token.substr(0, separatorIdx));
      tokenElement.setAttribute('value', token.substr(separatorIdx + 1));
    }

    // Cookieではなくメモリ上にトークンを保存する
    TokenManager.token = token;
  },
  getToken: function () {
    // hiddenがあればそれを使う
    const tokenElement = document.getElementById('#' + environment.webapi.tokenHeaderKey);
    if (tokenElement != null) {
      return tokenElement.getAttribute('name') + '=' + tokenElement.getAttribute('value');
    }

    return TokenManager.token;
  }
};

@Injectable()
export class WebApiService {

  constructor(private router: Router, private http: Http) { }

  /**
   * POSTでAPI呼び出しを行う.
   * @param uri リクエストURI
   * @param parameter リクエストパラメータ
   */
  callAjaxPost(uri: string, parameter: {}): Observable<CfResponse> {

    return this.callApi(uri, parameter, RequestMethod.Post);
  }

  /**
   * リクエストタイプに応じてAPI呼び出しを行う.
   * @param uri リクエストURI
   * @param parameter リクエストパラメータ
   * @param type リクエストタイプ
   */
  private callApi(uri, parameter, type): Observable<CfResponse> {

    const headers = this.createHeader();
    if (type === RequestMethod.Post) {
      headers.append('Content-Type', 'application/x-www-form-urlencoded');
    }
    const requestOptions = {
      headers: headers,
    };
    switch (type) {
      case RequestMethod.Post:
        return this.http.post(uri, this.stringfyFormURLEncoded(parameter), new RequestOptions(requestOptions))
          .map(this.succeedCallApi)
          .catch(error => {
            // ステータスコードが2xx以外はcatch句に入るので、
            // 受けたステータスコードのエラー画面へ遷移させる。
            this.router.navigate(['/error', error.status]);

            return Observable.throw(error.status);
          });
    }
  }

  /**
   * POSTでAPI呼び出しを行う.
   * 電話認証時400番台エラーポーリング処理ハンドリング
   * @param uri リクエストURI
   * @param parameter リクエストパラメータ
   */
  callAjaxPostforPolling(uri: string, parameter: {}): Observable<CfResponse> {

    return this.callApiforPolling(uri, parameter, RequestMethod.Post);
  }

  /**
   * リクエストタイプに応じてAPI呼び出しを行う.
   * 電話認証時400番台エラーポーリング処理ハンドリング
   * @param uri リクエストURI
   * @param parameter リクエストパラメータ
   * @param type リクエストタイプ
   */
  private callApiforPolling(uri, parameter, type): Observable<CfResponse> {

    const headers = this.createHeader();
    if (type === RequestMethod.Post) {
      headers.append('Content-Type', 'application/x-www-form-urlencoded');
    }
    const requestOptions = {
      headers: headers,
    };
    switch (type) {
      case RequestMethod.Post:
        return this.http.post(uri, this.stringfyFormURLEncoded(parameter), new RequestOptions(requestOptions))
          .map(this.succeedCallApi)
          .catch(error => {
            // ステータスコードが2xx以外はcatch句に入るので、
            // 受けたステータスコードをそのまま返却する。（画面遷移しない）
            return Observable.throw(error);
          });
    }
  }

  /**
   * リクエストヘッダーを作成する.
   */
  private createHeader(): Headers {
    const headers = new Headers();

    // セッションはヘッダーに格納しない
    // const sid = SessionManager.getSessionId();
    // if (sid) {
    //   headers.append(environment.webapi.sessionHeaderKey, sid);
    // }

    const token = TokenManager.getToken();
    if (token) {
      headers.append(environment.webapi.tokenHeaderKey, token);
    }
    headers.append(environment.webapi.useCookieHeaderKey, String(environment.webapi.useCookie));
    return headers;
  }

  /**
   * API呼び出しの成功時に呼ばれるメソッド.
   * Responseを引数に受けてCfRespopnseに変換する.
   * @param res レスポンス
   */
  private succeedCallApi(res: Response): CfResponse {

    const sid = res.headers.get(environment.webapi.sessionHeaderKey);
    if (sid) {
      SessionManager.setSessionId(sid);
    }

    const token = res.headers.get(environment.webapi.tokenHeaderKey);
    if (token) {
      TokenManager.setToken(token);
    }

    const data = res.json();

    return new CfResponse(data, res.status);
  }

  /**
   * JSONオブジェクトをx-www-form-urlencoded形式の文字列に変換する.
   * @param parameter パラメータ
   */
  private stringfyFormURLEncoded(parameter: {}): string {

    const array = [];
    for (const key of Object.keys(parameter)) {
      if (parameter[key] != null) {
        array.push(key + '=' + encodeURIComponent(parameter[key]));
      }
    }
    return array.join('&');

  }
}
